import { type RegisteredRouter, type RouteIds, useMatches } from "@tanstack/react-router"
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react"
import { z } from "zod"

import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { type Filter, upgradeLegacyFilterValues } from "~/utils/filter"

const filterSchema: z.ZodType<Filter> = z.object({
  field: z.string(),
  operator: z.string(),
  value: z.string(),
  by: z.string(),
  values: z.array(z.string()),
  gate: z.enum(["and", "or"]).optional(),
  groupId: z.number().optional(),
})

const filtersSchema: z.ZodType<Filter[]> = z.array(filterSchema)

const EMPTY_FILTERS: Filter[] = []

type FilterScopeContextValue = {
  filters: Filter[]
  setFilters: (filters: Filter[]) => Promise<Filter[]>
  clearAll: () => Promise<void>
}

const FilterScopeContext = createContext<FilterScopeContextValue | undefined>(undefined)

export type FilterScopeProviderProps = {
  children: ReactNode
  /**
   * Explicit scope key override. When provided, filters are keyed by this string instead
   * of the deepest matched routeId. Use to share a single filter scope across sibling tab
   * routes that should behave as one page (e.g. a NOOS buy's Quantities and Capacity tabs).
   */
  scopeKey?: string
}

const useStoredFilters = (pageKey: string): FilterScopeContextValue => {
  const [storedFilters, setStoredFilters] = useSafeLocalStorage<Filter[]>(
    `filters.${pageKey}`,
    EMPTY_FILTERS,
    filtersSchema,
  )
  const filters = useMemo(() => upgradeLegacyFilterValues(storedFilters), [storedFilters])

  const setFilters = useCallback(
    async (next: Filter[]) => {
      setStoredFilters(next)
      return next
    },
    [setStoredFilters],
  )

  const clearAll = useCallback(async () => {
    setStoredFilters(EMPTY_FILTERS)
  }, [setStoredFilters])

  return useMemo(() => ({ filters, setFilters, clearAll }), [filters, setFilters, clearAll])
}

/**
 * Provides a per-route filter scope. The pageKey is derived from TanStack Router's
 * deepest matched routeId (template path with `$param` placeholders intact), so two
 * URLs that share a route definition (e.g. /demand-planning/fashion/abc/demand and
 * /demand-planning/fashion/xyz/demand) share filters, while sibling tabs do not.
 *
 * Pass `scopeKey` to override this and unify sibling tabs under one scope.
 *
 * Persistence is delegated to useSafeLocalStorage, which scopes keys to the current
 * organization via a `{orgId}.` prefix.
 */
export const FilterScopeProvider = ({ children, scopeKey }: FilterScopeProviderProps) => {
  const matches = useMatches()
  const pageKey = scopeKey ?? matches[matches.length - 1]?.routeId ?? "__unknown_route__"
  const value = useStoredFilters(pageKey)

  return <FilterScopeContext.Provider value={value}>{children}</FilterScopeContext.Provider>
}

export const useFilterScope = (): FilterScopeContextValue => {
  const ctx = useContext(FilterScopeContext)
  if (!ctx) {
    throw new Error("useFilterScope must be used inside a <FilterScopeProvider>")
  }
  return ctx
}

/**
 * Another route's filter scope, for a link that sets the filters a sibling tab opens with before
 * navigating there. The target route must key its scope on its own id: the app-wide default, or
 * `scopeKey={Route.id}`.
 */
export const useRouteFilterScope = (
  routeId: RouteIds<RegisteredRouter["routeTree"]>,
): FilterScopeContextValue => useStoredFilters(routeId)
